import React, { useState } from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Brand } from "./Brand";
import ResponsiveDialog from "./ResponsiveDialog";

export default function AuthGate({ open, onOpenChange, onSuccess }) {
  const { login, signup } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", country: "CY" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back");
      } else {
        await signup(form);
        toast.success("Account created");
      }
      onSuccess?.();
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Auth failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange} testId="auth-gate-dialog">
      <div className="space-y-3 pt-2">
        <div className="flex justify-center"><Brand /></div>
        <h2 className="font-display text-2xl text-center font-bold text-primary">
          {mode === "login" ? "Sign in to continue" : "Create your account"}
        </h2>
        <p className="text-sm text-slate-500 text-center -mt-1">
          Required to lock your rate, complete KYC, and track your order.
        </p>
      </div>
      <form onSubmit={submit} className="space-y-4 mt-5">
        {mode === "signup" && (
          <>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Full name</Label>
              <Input
                required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-xl" data-testid="auth-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Country (ISO)</Label>
              <Input
                required value={form.country} maxLength={2}
                onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })}
                className="rounded-xl" data-testid="auth-country-input"
              />
            </div>
          </>
        )}
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</Label>
          <Input
            required type="email" value={form.email} autoComplete="email"
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="rounded-xl" data-testid="auth-email-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Password</Label>
          <Input
            required type="password" minLength={6} value={form.password}
            autoComplete={mode === "login" ? "current-password" : "new-password"}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="rounded-xl" data-testid="auth-password-input"
          />
        </div>
        <Button
          type="submit" disabled={loading}
          className="w-full bg-accent hover:bg-accent/90 text-white rounded-xl h-12 font-display font-semibold"
          data-testid="auth-submit-btn"
        >
          {loading ? "Working…" : mode === "login" ? "Sign in" : "Create account"}
        </Button>
        <button
          type="button" onClick={() => setMode(mode === "login" ? "signup" : "login")}
          className="w-full text-sm text-slate-500 hover:text-primary transition-colors"
          data-testid="auth-toggle-mode"
        >
          {mode === "login" ? "Need an account? Sign up" : "Already have an account? Sign in"}
        </button>
      </form>
    </ResponsiveDialog>
  );
}
